import { Inject } from '@odinjs/odin';

import Controller, { OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ToastService } from '@philips-internal/oswald-ui-components';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HttpResponse, HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FieldAPI, FormAPI, InputAPI, PanelAPI } from '@philips-internal/oswald-ui-components/lib/components';

import { Endpoint } from '../../../../constants/endpoints';
import { FragmentNames, RecommendationViewFragmentAliases } from '../../../../codegen';
import BaseController from '../../../../controllers/BaseController';
import InformationBarUtils from '../../../../controllers/commons/header/information-bar-header-utils';
import { RecommendationsViewResponseType } from './recommendations-types';
import { AdministrationType, DurationType } from '../cpoe-view-types';

@Controller({ fragment: FragmentNames.RECOMMENDATION_VIEW_FRAGMENT })
class RecommendationsViewController extends BaseController {

  @Inject(RecommendationViewFragmentAliases.PANEL_LOADER)
  panelLoaderAPI!: PanelAPI;

  @Inject(RecommendationViewFragmentAliases.PANEL_CONTENT)
  panelContentAPI!: PanelAPI;

  @Inject(RecommendationViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  formMainInformationsAPI!: FormAPI;

  @Inject(RecommendationViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  formPeriodOfValidityAPI!: FormAPI;

  @Inject(RecommendationViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  formAdicionalMainInformationsAPI!: FormAPI;

  @Inject(RecommendationViewFragmentAliases.FIELD_END_DATE)
  fieldEndDateAPI!: FieldAPI;

  @Inject(RecommendationViewFragmentAliases.FIELD_HOURS)
  fieldHoursAPI!: FieldAPI;

  @Inject(RecommendationViewFragmentAliases.INPUT_START_DATE)
  inputStartDateAPI!: InputAPI;

  @Inject(RecommendationViewFragmentAliases.INPUT_END_DATE)
  inputEndDateAPI!: InputAPI;

  @OnMount(RecommendationViewFragmentAliases.PANEL_CONTENT)
  handleMountPanelCard(): void {
    InformationBarUtils.collapsedBar = true;
    const { recommendationId, patientId  } = this.historyService.getCurrentRouteParams()
    this.fetchRecommendationsDetails(String(recommendationId), String(patientId))
  }

  @OnMount(RecommendationViewFragmentAliases.FORM_MAIN_INFORMATIONS)
  handleMountFormMainInformation(): void {
    const recommendationsView = this.context.get('recommendationsView') as RecommendationsViewResponseType
    if(recommendationsView) {
      this.formMainInformationsAPI.setValue({
        intervalDescription: recommendationsView.intervalDescription,
        recommendations: recommendationsView.recommendations,
      })
    }
  }

  @OnMount(RecommendationViewFragmentAliases.FORM_PERIOD_OF_VALIDITY)
  handleMountFormPeriodOfValidity(): void {
    const recommendationsView = this.context.get('recommendationsView') as RecommendationsViewResponseType
    if(recommendationsView) {
      this.formPeriodOfValidityAPI.setValue({
        administration: recommendationsView.administration,
        duration: recommendationsView.duration,
        endDate: recommendationsView.endDate,
        hours: recommendationsView.hours,
        startDate: recommendationsView.startDate,
      })
      this.inputStartDateAPI.setValue(String(recommendationsView.startDate))
      this.inputEndDateAPI.setValue(String(recommendationsView.endDate))
      this.showFieldsByDurationType(recommendationsView.durationType);
      this.showFieldsByAdministrationType(recommendationsView.administrationType);
    }
  }

  @OnMount(RecommendationViewFragmentAliases.FORM_ADDITIONAL_INFORMATION)
  handleMountFormAdicionalInformations(): void {
    const recommendationsView = this.context.get('recommendationsView') as RecommendationsViewResponseType
    if(recommendationsView) {
      this.formAdicionalMainInformationsAPI.setValue({
        justification: recommendationsView.justification,
        observation: recommendationsView.observation,
        prescriberDt: recommendationsView.prescriberDt,
        prescriberName: recommendationsView.prescriberName,
      })
    }
  }

  private showFieldsByDurationType(duration: string): void {
    this.fieldEndDateAPI.setVisible(duration === DurationType.PROGRAMMED)
  }

  private showFieldsByAdministrationType(administration: string): void {
    this.fieldHoursAPI.setVisible(administration === AdministrationType.AS_START_TIME)
  }

  private fetchRecommendationsDetails(recommendationId: string, patientId: string): void {
    this.panelLoaderAPI.setVisible(true)
    this.panelContentAPI.setVisible(false)
    HttpService.get(Endpoint.GET_CPOE_RECOMMENDATIONS_DETAILS, { patientId, recommendationId })
      .then((response: HttpResponse) => {
        const recommendationsView = response.body as RecommendationsViewResponseType;
        this.context.set({ recommendationsView })
      }).catch((err) => {
        this.showToast(err?.body, ToastLevel.ERROR)
      }).finally(() => {
        this.panelLoaderAPI.setVisible(false)
        this.panelContentAPI.setVisible(true)
      })
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-pews-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }
}

export default RecommendationsViewController;